import DeviceControls from 'components/DeviceControls/DeviceControls';
import DeviceHistory from 'components/DeviceHistory/DeviceHistory';
import GroupSettingsDeviceList from 'components/GroupSettingsDeviceList/GroupSettingsDeviceList';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import DeviceMap from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { getGroupMapBounds } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/utils/DeviceMapUtils';
import {
  GROUP_HISTORY_PAGE_SIZE,
  GROUP_HISTORY_START_MS,
} from 'features/Integrations/features/ConnectedDeviceGroups/features/GroupSettingsModal/utils/groupSettingsModalUtils';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { useEffect, useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { MdExpandMore } from 'react-icons/md';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { ErrorMessage } from './ErrorMessage';
import WidgetLoadingAnimation from './WidgetLoadingAnimation';
import WidgetTimeUpdate from './WidgetTimeUpdate';
import styles from './DeviceGroupWidget.module.css';

const EMPTY_DEVICES = [];

export default function DeviceGroupWidget({ dimensions }) {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState({ state: false, message: '' });
  const ROUTE = 'deviceGroupList';
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [virtualDevice, setVirtualDevice] = useState();
  const [sending, setSending] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const {
    data: connectedDevices = EMPTY_DEVICES,
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups,
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  const groups = deviceGroups ?? [];
  const selectedGroup = groups.find((group) => group.id === selectedGroupId) ?? groups[0] ?? null;
  const groupDevices = useMemo(() => {
    if (!selectedGroup?.devices?.length) return [];
    const idSet = new Set(selectedGroup.devices);
    return connectedDevices.filter((device) => idSet.has(device.id));
  }, [connectedDevices, selectedGroup]);

  const deviceMapBounds = useMemo(() => getGroupMapBounds(groupDevices), [groupDevices]);

  // "Group Device History" lists the group's command events. The page load
  // brings one newest row per device; the rest is asked for when the accordion
  // opens, for the group's non-Stage devices only, a page at a time.
  const historyDeviceIds = useMemo(
    () =>
      groupDevices.filter((d) => (d.device_type || '').toLowerCase() !== 'stage').map((d) => d.id),
    [groupDevices],
  );
  // The window starts at the epoch, the same as the group modal and the group
  // settings panel: these are command devices, so a bounded window is empty for
  // one that has been idle, and GROUP_HISTORY_PAGE_SIZE is what bounds the read.
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: historyDeviceIds,
    startTime: GROUP_HISTORY_START_MS,
    limit: GROUP_HISTORY_PAGE_SIZE,
    enabled: isHistoryExpanded,
  });

  // Devices in the selected group that are sirens (memoized)
  const sirenDevices = useMemo(
    () =>
      (connectedDevices || []).filter(
        (d) =>
          selectedGroup?.devices?.includes(d.id) &&
          d.device_type === 'Technology Bridge_Siren/Giant Voice',
      ),
    [connectedDevices, selectedGroup],
  );

  const highlightIds = virtualDevice?.audio?.device_ids ?? [];

  const groupOptions = useMemo(() => groups.map((g) => ({ value: g.id, label: g.name })), [groups]);

  const hasDevices = (connectedDevices?.length || 0) > 0;

  const onGroupChange = (groupId) => {
    setSelectedGroupId(groupId);
  };

  const groupSelector = (
    <div className="row align-items-center g-2">
      <div className="col-4 text-nowrap">Group Alias:</div>
      <div className="col-8 cursor-pointer">
        <BaseSelect
          isSearchable
          selectProps={{
            'aria-label': 'Group Alias',
            options: groupOptions,
            value: findSelectOption(groupOptions, selectedGroup?.id),
            placeholder: 'Select',
            onChange: (option) => onGroupChange(option?.value ?? null),
          }}
        />
      </div>
    </div>
  );

  const renderListAndHistory = (deviceListHeight) => (
    <div className="d-flex flex-column gap-2 min-h-0">
      <div className="flex-shrink-0" style={{ height: deviceListHeight, minHeight: 160 }}>
        <GroupSettingsDeviceList
          devices={groupDevices}
          highlightIds={highlightIds}
          tableLayout
          enableTableOptions
          hideUnavailableLiveAudio
          compactTable
          tablePreferenceKey="connected-device-groups-widget-devices"
        />
      </div>

      {virtualDevice?.data?.length > 0 && (
        <Accordion
          activeKey={isHistoryExpanded ? '0' : null}
          onSelect={(eventKey) => setIsHistoryExpanded(eventKey === '0')}
          className="rounded-3 overflow-hidden"
        >
          <Accordion.Item eventKey="0" className="rounded-3 overflow-hidden">
            <Accordion.Header className={styles.historyHeader}>
              <span className="d-flex w-100 align-items-center justify-content-between gap-2 pe-1">
                <span>Group Device History</span>
                <MdExpandMore
                  className={`${styles.historyChevron} ${
                    isHistoryExpanded ? styles.historyChevronExpanded : ''
                  }`}
                  aria-hidden="true"
                />
              </span>
            </Accordion.Header>
            <Accordion.Body className="p-2">
              <div className="w-100 overflow-auto" style={{ maxHeight: 240 }}>
                <DeviceHistory
                  title="Group Device History"
                  data={virtualDevice.data}
                  deviceType={virtualDevice.device_type}
                  hideHeader
                  tablePreferenceKey="connected-device-groups-widget-history"
                  tableOptionsClassName="px-1 pt-1"
                  loading={historyLoading}
                  hasMore={historyHasMore}
                  onLoadMore={loadMoreHistory}
                  error={historyError}
                />
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );

  useEffect(() => {
    if (!selectedGroup) return;
    const devices = selectedGroup.devices
      .map((deviceId) => connectedDevices?.find((device) => device.id === deviceId))
      .filter((device) => !!device);

    let device = devices.find((device) => {
      //check if device type is correct
      const type = selectedGroup.type;
      let typeCheck = true;

      if (type === 'Technology Bridge_Flashing Lights')
        typeCheck = ['Technology Bridge_Flashing Lights', 'Manual Road Closure'].includes(
          device.device_type,
        );
      else if (type !== '') typeCheck = device.device_type === type;

      if (selectedGroup.devices?.includes(device.id))
        return selectedGroup.devices?.includes(device.id) && typeCheck;
    });

    const data = connectedDevices
      ?.filter(
        (_device) =>
          selectedGroup.devices?.includes(_device.id) &&
          _device.device_type.toLowerCase() !== 'stage',
      )
      .map((_device) => _device?.data)
      .flatMap((v) => v)
      .sort((a, b) => b.ts - a.ts);

    setVirtualDevice({
      ...device,
      data: data,
      device_type: selectedGroup?.type || device?.device_type || '',
    });
  }, [selectedGroup, connectedDevices]);

  const deviceControlListener = async (evt, data) => {
    switch (evt) {
      case 'update':
        setVirtualDevice(data);
        return;
      case 'submit':
        if (!selectedGroup?.id) return;
        setSending(true);
        try {
          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments,
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments,
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
        } catch (error) {
          console.error(error.message);
        }
        setSending(false);
        return;
      case 'cancel-audio':
        if (!selectedGroup?.id) return;
        try {
          await sendDeviceCommand(
            {
              id: virtualDevice.id,
              node_id: virtualDevice.node_id,
              system_type: virtualDevice.system_type,
              device_type: virtualDevice.device_type,
            },
            7,
            '',
            false,
          );
        } catch (err) {
          console.error('Error sending device command:', err.message);
        }
        return;
      case 'audio-manager':
        return;
      default:
        return;
    }
  };

  const GroupControls =
    selectedGroup?.devices?.length > 0 && selectedGroup?.devices[0] ? (
      <DeviceControls
        device={virtualDevice}
        devices={sirenDevices}
        on={deviceControlListener}
        sending={sending}
        audioOverride={audioOverride}
        audioManagerButtonEnabled={false}
        compact
      />
    ) : (
      <div>Please select a valid group.</div>
    );

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  if (connectedDevicesLoading || deviceGroupsLoading)
    return <WidgetLoadingAnimation widgetName={'ConnectedDeviceGroups'} dimensions={dimensions} />;

  if ((dimensions?.[1] || 0) >= 2) {
    return (
      <div className="d-flex flex-column h-100 min-h-0 overflow-hidden">
        <WidgetTimeUpdate route={ROUTE} realtime />
        {hasDevices ? (
          <div className="d-flex flex-column flex-grow-1 min-h-0 overflow-y-auto overflow-x-hidden px-1 pb-1 gap-2">
            <div
              className="w-100 overflow-hidden rounded-2 flex-shrink-0"
              style={{ height: '42%', minHeight: 240, maxHeight: 360 }}
            >
              <DeviceMap
                mode="group"
                devicesOverride={groupDevices}
                mapBoundsOverride={deviceMapBounds}
                filtersCompact
              />
            </div>
            {groupSelector}

            {GroupControls}

            {renderListAndHistory(240)}
          </div>
        ) : (
          <div className="d-flex flex-grow-1 justify-content-center align-items-center">
            <p className="text-muted m-0">No connected devices available.</p>
          </div>
        )}
      </div>
    );
  }

  if (dimensions[0] === 2 && dimensions[1] === 1) {
    return (
      <div className="d-flex flex-column h-100 min-h-0 overflow-hidden">
        <WidgetTimeUpdate route={ROUTE} realtime />
        {hasDevices ? (
          <div className="d-flex flex-column flex-grow-1 min-h-0 overflow-hidden px-1 pt-2 pb-1 gap-2">
            <div
              className="d-flex flex-row gap-3 align-items-stretch min-h-0 overflow-hidden flex-shrink-0"
              style={{ flexBasis: '58%', maxHeight: '58%' }}
            >
              <div
                className="h-100 overflow-hidden rounded-2 min-w-0"
                style={{
                  flex: '0 0 60%',
                  maxWidth: '60%',
                  boxSizing: 'border-box',
                }}
              >
                <DeviceMap
                  mode="group"
                  devicesOverride={groupDevices}
                  mapBoundsOverride={deviceMapBounds}
                  filtersCompact
                />
              </div>

              <div
                className="h-100 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden pe-2"
                style={{ flex: '1 1 40%', maxWidth: '40%' }}
              >
                <div className="d-flex flex-column gap-2 min-h-0 pb-2">
                  {groupSelector}

                  {GroupControls}
                </div>
              </div>
            </div>

            <div className="w-100 flex-grow-1 min-h-0 overflow-y-auto overflow-x-hidden">
              {renderListAndHistory(170)}
            </div>
          </div>
        ) : (
          <div className="d-flex flex-grow-1 justify-content-center align-items-center">
            <p className="text-muted m-0">No connected devices available.</p>
          </div>
        )}
      </div>
    );
  }
}
